/**
 * Azure Search Indexes API Hook
 *
 * Provides CRUD operations for Azure Search Indexes.
 * Backend: AzureIndexesController.cs in velaro-messaging
 */

import { useCallback } from "react";
import useApi from "./useApi";
import { AzureIndex } from "@/pages/Messaging/Bots/types";

export interface CreateIndexRequest {
  name: string;
  type: string;
  searchSize: number;
  semanticSearchEnabled: boolean;
}

export interface EditIndexRequest {
  id: number;
  name: string;
  type: string;
  searchSize: number;
  semanticSearchEnabled: boolean;
}

export interface ApiResponse<T = void> {
  success: boolean;
  data?: T;
  error?: string;
}

export default function useAzureIndexesApi() {
  const api = useApi();

  /**
   * Create a new Azure Search Index
   *
   * Backend: POST /AzureIndexes/CreateIndex
   *
   * Requirements:
   * - Requires files in blob storage ({siteId}/ai-upload/)
   * - Checks subscription limits (MaxIndexCount)
   * - Name will be lowercased
   */
  const createIndex = useCallback(
    async (request: CreateIndexRequest): Promise<ApiResponse<AzureIndex>> => {
      try {
        const response = await api.messaging.post("AzureIndexes/CreateIndex", {
          name: request.name.toLowerCase(),
          type: request.type,
          searchSize: request.searchSize,
          semanticSearchEnabled: request.semanticSearchEnabled
        });

        if (response.status === 200) {
          const data = await response.json();
          return { success: true, data };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Failed to create index" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  /**
   * Edit an existing Azure Search Index
   *
   * Backend: POST /AzureIndexes/EditIndex
   */
  const editIndex = useCallback(
    async (request: EditIndexRequest): Promise<ApiResponse<AzureIndex>> => {
      try {
        const response = await api.messaging.post("AzureIndexes/EditIndex", {
          id: request.id,
          name: request.name,
          type: request.type,
          searchSize: request.searchSize,
          semanticSearchEnabled: request.semanticSearchEnabled
        });

        if (response.status === 200) {
          const data = await response.json();
          return { success: true, data };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Failed to update index" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  /**
   * Delete an Azure Search Index
   *
   * Backend: DELETE /AzureIndexes/DeleteIndex/{id}
   *
   * Note: Will fail if any AI configurations are using this index
   */
  const deleteIndex = useCallback(
    async (id: number): Promise<ApiResponse> => {
      try {
        const response = await api.messaging.delete(
          `AzureIndexes/DeleteIndex/${id}`
        );

        if (response.status === 200) {
          return { success: true };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Failed to delete index" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  /**
   * Query an index (for testing)
   *
   * Backend: POST /AzureIndexes/QueryIndex
   */
  const queryIndex = useCallback(
    async (indexId: number, query: string): Promise<ApiResponse<string>> => {
      try {
        const response = await api.messaging.post("AzureIndexes/QueryIndex", {
          indexId,
          query
        });

        if (response.status === 200) {
          const data = await response.text();
          return { success: true, data };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Query failed" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  /**
   * Publish a scraper job's content to an index
   *
   * Backend: POST /AzureIndexes/PublishJob
   * - Calls Python scraper to copy job content to ai-upload/{indexName}/
   */
  const publishJob = useCallback(
    async (indexId: number, jobId: string): Promise<ApiResponse> => {
      try {
        const response = await api.messaging.post("AzureIndexes/PublishJob", {
          indexId,
          jobId
        });

        if (response.status === 200) {
          const data = await response.json();
          return { success: true, data };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Failed to publish job" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  /**
   * Unpublish a scraper job's content from an index
   *
   * Backend: POST /AzureIndexes/UnpublishJob
   * - Calls Python scraper to remove job content from ai-upload/{indexName}/
   */
  const unpublishJob = useCallback(
    async (indexId: number, jobId: string): Promise<ApiResponse> => {
      try {
        const response = await api.messaging.post("AzureIndexes/UnpublishJob", {
          indexId,
          jobId
        });

        if (response.status === 200) {
          const data = await response.json();
          return { success: true, data };
        }

        const errorText = await response.text();
        return { success: false, error: errorText || "Failed to unpublish job" };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : "Network error"
        };
      }
    },
    [api.messaging]
  );

  return {
    createIndex,
    editIndex,
    deleteIndex,
    queryIndex,
    publishJob,
    unpublishJob
  };
}
